import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaPaperPlane } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

export default function OrderForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        company: "",
        contactName: "",
        email: "",
        phone: "",
        product: "Hardwood Charcoal",
        tonnage: "",
        packaging: "2.5 kg",
        location: "",
        message: "",
    });

    const [sent, setSent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");

    const getOrdersEndpoint = () => {
        const raw = (process.env.REACT_APP_API_URL || '').trim();
        if (!raw) return '/api/orders';

        // If user provided a relative path, use it as-is.
        if (raw.startsWith('/')) return raw;

        // If user provided a host like "localhost:5000", add a scheme.
        const withScheme = raw.includes('://') ? raw : `http://${raw}`;

        try {
            const url = new URL(withScheme);
            const normalizedPath = url.pathname.replace(/\/+$/, '');

            // If env var is a base URL, append the orders route.
            if (!normalizedPath || normalizedPath === '') {
                url.pathname = '/api/orders';
            } else if (normalizedPath === '/') {
                url.pathname = '/api/orders';
            } else if (!normalizedPath.endsWith('/api/orders')) {
                url.pathname = `${normalizedPath}/api/orders`.replace(/\/{2,}/g, '/');
            } else {
                url.pathname = normalizedPath;
            }

            return url.toString();
        } catch {
            return '/api/orders';
        }
    };

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        try {
            // Validate required fields
            if (!form.contactName || !form.email || !form.phone || !form.location) {
                throw new Error("Please fill in all required fields");
            }

            // Map form fields to match Order model schema
            const orderData = {
                name: form.contactName,
                email: form.email,
                phone: form.phone,
                product: form.product,
                packaging: form.packaging,
                quantity: parseInt(form.tonnage) || 1, // Parse tonnage as number, default to 1 if invalid
                address: form.location,
                message: form.message,
            };

            // Send POST request to server
            const apiUrl = getOrdersEndpoint();
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const result = await response.json();
            console.log("Order created:", result);

            // Helpful for GTM / analytics triggers (no PII).
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "quote_form_submitted",
                product: form.product,
                quantity: form.tonnage,
                packaging: form.packaging
            });

            setIsSubmitting(false);
            setSent(true);
            sessionStorage.setItem('vsgrill_order_submitted', '1');
            navigate("/thank-you");

            // Reset form after showing success message
            setTimeout(() => {
                setSent(false);
                setForm({
                    company: "",
                    contactName: "",
                    email: "",
                    phone: "",
                    product: "Hardwood Charcoal",
                    tonnage: "",
                    location: "",
                    message: "",
                });
            }, 5000);
        } catch (error) {
            console.error("Error creating order:", error);
            setIsSubmitting(false);
            setError(error.message);
        }
    };

    return (
        <section id="quote" className="py-20 px-6 bg-gradient-to-b from-vsblackLight to-vsblack">
            <div className="max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl md:text-5xl font-bold font-serif mb-4">
                        <span className="text-vsred hero-text-shadow">{t('form.title')}</span>
                    </h2>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        {t('form.subtitle')}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="card-dark p-8 md:p-12"
                >
                    <AnimatePresence mode="wait">
                        {sent ? (
                            <motion.div
                                key="success"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                className="text-center py-12"
                            >
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                                    className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center shadow-lg"
                                >
                                    <FaCheckCircle className="text-4xl text-white" />
                                </motion.div>
                                <h3 className="text-3xl font-bold text-white mb-4 font-serif">
                                    {t('form.success.title')}
                                </h3>
                                <p className="text-gray-400 text-lg mb-2">
                                    {t('form.success.message')}
                                </p>
                                <p className="text-gray-500">
                                    {t('form.success.description')}
                                </p>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                initial={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onSubmit={handleSubmit}
                                className="space-y-6"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                    {/* Contact Person */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            {t('form.contactPerson')} *
                                        </label>
                                        <input
                                            required
                                            name="contactName"
                                            value={form.contactName}
                                            onChange={handleChange}
                                            placeholder={t('form.placeholder.name')}
                                            className="input-dark w-full"
                                        />
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Email *
                                        </label>
                                        <input
                                            required
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            placeholder={t('form.placeholder.email')}
                                            className="input-dark w-full"
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            {t('form.phone')} *
                                        </label>
                                        <input
                                            required
                                            name="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={handleChange}
                                            placeholder={t('form.placeholder.phone')}
                                            className="input-dark w-full"
                                        />
                                    </div>

                                    {/* Product */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            {t('form.productType')} *
                                        </label>
                                        <select
                                            name="product"
                                            value={form.product}
                                            onChange={handleChange}
                                            className="input-dark w-full"
                                        >
                                            <option value="Hardwood Charcoal for grilling and BBQ">{t('form.products.hardwood')}</option>
                                            <option value="Hardwood Charcoal with restaurant quality">{t('form.products.restaurant')}</option>
                                        </select>
                                    </div>

                                    {/* Tonnage */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            {t('form.requiredTonnage')} *
                                        </label>
                                        <input
                                            required
                                            name="tonnage"
                                            value={form.tonnage}
                                            onChange={handleChange}
                                            placeholder={t('form.placeholder.tonnage')}
                                            className="input-dark w-full"
                                        />
                                    </div>
                                    {/* Packaging Type */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            {t('form.packagingType')} *
                                        </label>
                                        <select
                                            required
                                            name="packaging"
                                            value={form.packaging}
                                            onChange={handleChange}
                                            className="input-dark w-full">
                                            <option value="2.5 kg">2.5 kg</option>
                                            <option value="10 kg">10 kg</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Delivery Location */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        {t('form.deliveryLocation')}
                                    </label>
                                    <input
                                        name="location"
                                        value={form.location}
                                        onChange={handleChange}
                                        placeholder={t('form.placeholder.location')}
                                        className="input-dark w-full"
                                    />
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        {t('form.additionalRequirements')}
                                    </label>
                                    <textarea
                                        name="message"
                                        value={form.message}
                                        onChange={handleChange}
                                        placeholder={t('form.placeholder.requirements')}
                                        className="input-dark w-full h-32 resize-none"
                                    ></textarea>
                                </div>

                                {/* Error Display */}
                                {error && (
                                    <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
                                        <p className="text-red-400 text-sm">
                                            {error === "Failed to fetch" ? t('form.error.generic') :
                                                error.includes("fill in all required fields") ? t('form.error.validation') :
                                                    error}
                                        </p>
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-800">
                                    <p className="text-sm text-gray-400">
                                        {t('form.footer')}
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="btn-primary flex items-center gap-3 min-w-[200px] justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                                                />
                                                <span>{t('form.sending')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane />
                                                <span>{t('form.submit')}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}
